import { Component, ElementRef, HostListener, Injector, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { MessageService } from 'primeng/api';
import { BehaviorSubject, from, lastValueFrom, NEVER, Observable, of, Subject, Subscription, defer } from 'rxjs';
import { filter, map, switchMap, tap, finalize } from 'rxjs/operators';
import { debounce } from 'underscore';
import { cloneDeep } from 'lodash';

import { MediaServiceApi } from '../../../../api/mediaService/media-service.api';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { DeliverySchedule, ScheduleType } from '../../../../Common/Forms/components/DeliveryScheduleInput/models/delivery-schedule';
import { FormMessageComponent } from '../../../../Common/Forms/components/FormMessage/form-message.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { RadioTypesEnum } from '../../../../Common/Forms/enums/radio-types.enum';
import { FileUploadModel } from '../../../../Common/Forms/models/file-upload.model';
import { RightSidebarComponent } from '../../../../Common/Layout/components/RightSidebar/right-sidebar.component';
import { TabComponent } from '../../../../Common/Layout/components/Tab/tab.component';
import { attributeTypesEnum } from '../../../../Common/Misc/enums/attribute-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { AttributeModel } from '../../../../Common/Misc/models/attribute.model';
import { BlockModel } from '../../../../Common/PageBuilder/models/block.model';
import { AbstractPageBuilderService } from '../../../../Common/PageBuilder/services/abstract-page-builder.service';
import { DistributionStatus } from '../../../../Common/TargetingShared/enum/distribution-status.enum';
import { DistributionErrors, DistributionService, DistributionValidationLevel } from '../../../../Common/TargetingShared/services/distribution.service';
import { ActivatedRoute } from '@angular/router';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { DiscoverRouteModel } from '../../../DiscoverRouter/models/discover-route.model';
import { DiscoverRoutingService } from '../../../DiscoverRouter/services/discover-routing.service';
import { DistributionBlockTypeEnum } from '../../enums/distribution-block-type.enum';
import { DistributionModel } from '../../models/distribution.model';
import { RecipientInformationModel } from '../../models/recipient-information.model';
import { DevicePreviewDialogComponent } from './DevicePreviewDialog/device-preview-dialog.component';
import { ScreenType } from './DevicePreviewDialog/screen-type.enum';
import { DistributionConfirmationComponent } from './DistributionConfirmation/distribution-confirmation.component';
import { PlainTextEditorDialogComponent } from './PlainTextEditorDialog/plain-text-editor-dialog.component';

import { FocusHelper } from '../../../../Common/Forms/helpers/focus.helper';
import { PageBuilderComponent } from '../../../../Common/PageBuilder/components/PageBuilder/page-builder.component';
import { EmailHelper } from '../../../../Common/PageBuilder/helpers/email.helper';
import { RichTextDisplayElementModel } from '../../../../Common/PageBuilder/models/rich-text-display-element.model';
import { DialogIoModelObservable } from '../../../../Core/Dialogs/models/dialog-io.model';
import { AIDistributionHeaderDialogComponent } from './AISubjectLineDialog/AI-distribution-header-dialog.component';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { HeaderEditorService } from '../../../../Common/TargetingShared/services/header-editor.service';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { DistributionBannerErrorTypeEnum, bannerErrorsMap } from '../../../../Discover/Targeting/enums/distribution-banner-error-type.enum';
import { DistributionSaveTemplateDialogComponent } from './DistributionSaveTemplateDialog/distribution-save-template-dialog.component';
import { DistributionStepIds, DistributionStepsEnum } from './DistributionStepsNavigation/distribution-steps-navigation.component';
import { RecipientType } from '../../enums/recipient-type.enum';
import { RecipientEditorService } from './RecipientEditor/recipient-editor.service';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { StepsBannerErrorConfigModel } from '../../../../Common/Misc/models/banner-error-config.model';
import { DistributionRecipientModel } from '../../models/distribution-recipient-model';
import { RecipientViewerComponent } from './RecipientEditor/RecipientViewer/recipient-viewer.component';
import { RecommendedRecipientService } from './RecipientEditor/RecommendedRecipient/recommended-recipient-service';
import { NewResourceDialogModel } from '../../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { RecipientGroup } from '../../models/recipient-group.model';
import { SearchModeEnum } from '../../../../Common/TargetingShared/enum/search-mode.enum';
import { TargetingSearch } from '../../models/targeting-search.model';
import { DisplayGroupModel } from '../../../../Common/Layout/models/display-group.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { LockSearchDialogComponent } from './LockSearchDialog/lock-search-dialog.component';
import { ToggleAction } from '../../enums/toggle-action.enum';
import { TargetingRhpContextsService } from '../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { FooterModel } from '../../../../Common/TargetingShared/model/footer.model';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';
import { MediaSummaryModel } from '../../../../Common/ContentLibrary/models/content-library-media-summary.model';
import {
    ManageContentLibraryDialogComponent
} from '../../../../Common/ContentLibrary/components/ManageContentLibraryDialog/manage-content-library-dialog.component';
import { OnlineBoostConfirmationDialogComponent } from './OnlineBoostConfirmationDialog/online-boost-confirmation-dialog.component';
import { AEORecommendationsDialogComponent } from './AEORecommendationsDialog/aeo-recommendations-dialog.component';

@Component({
    selector: 'agpr-distribution',
    templateUrl: './distribution.component.html',
    styleUrls: ['./distribution.component.scss', './DevicePreviewDialog/device-preview-dialog.component.scss'],
    providers: [
        {
            provide: MediaServiceApi,
            useClass: MediaServiceApi
        },
        {
            provide: HeaderEditorService,
            useClass: HeaderEditorService
        },
        {
            provide: FilterService,
            useExisting: RecipientEditorService
        }
    ],
    standalone: false
})
export class DistributionComponent implements OnInit, OnDestroy {
    private subscriptions: Subscription[] = [];
    private sendDistributionSubject$ = new Subject<DistributionModel>();

    private currentRoute: DiscoverRouteModel;
    public distribution: DistributionModel;
    public distributionDirty = false;

    public recipientInformation: RecipientInformationModel;

    // Later, will encapsulate the conditions for validity (unique name, has recipients, etc)
    public distributionSchedulable = true;

    public loading: boolean = false;

    public bannerErrorList: StepsBannerErrorConfigModel[];
    public compositionErrors: StepsBannerErrorConfigModel[] = [];

    public readonly scheduleTypes = ScheduleType;
    public readonly distributionStatuses = DistributionStatus;
    public readonly buttonTypes = ButtonTypesEnum;
    public readonly radioTypesEnum = RadioTypesEnum;
    public readonly screenTypes = ScreenType;
    public readonly distributionBlockType = DistributionBlockTypeEnum;
    public readonly messageSeverityEnum = messageSeverityEnum;
    public readonly agprComponentStyleEnum = AgprComponentStyleEnum;

    public selectedBlock: BlockModel;
    public selectedRhpTabIndex: number = 0;
    public personalizationRhpTabIndex: number = 0;
    public readonly defaultSelectedRhpTabIndex: number = 0;
    public readonly setupTabIndex: number = 0;
    public readonly blockFormTabIndex: number = 2;
    // @TODO: add general styles tab
    public readonly generalStylesTabIndex: number = 2;
    public readonly defaultEmailWidthPx = 640;
    public readonly maximumEmailWidthPx = 1200;
    public readonly minimumEmailWidthPx = 400;
    public readonly maxNumberOfAttachments = 5;
    public hasDuplicateAttachments = false;
    public readonly distributionStepsEnum = DistributionStepsEnum;
    public readonly distributionStepIds = DistributionStepIds;
    public readonly searchModeEnum = SearchModeEnum;
    readonly toggleAction = ToggleAction;

    public distributionAttributes: AttributeModel[] = [];

    overlayContainer: Element;
    attachments: FileUploadModel[] = [];
    errors: DistributionErrors = new DistributionErrors();
    headerError: string = '';
    recipientPanelError: string = '';
    nameError: string = '';
    isDefaultFooterDirty: boolean = false;
    maxWidthEnabled: boolean = true;
    maxWidthOutOfRange: boolean = false;
    distributionMaxWidthPx: number = this.defaultEmailWidthPx;
    debouncedUpdateDistributionMaxWidth = debounce((distributionMaxWidthPx) => this.updateDistributionMaxWidth(distributionMaxWidthPx), 500);

    selectedScreenType: ScreenType = undefined;

    testEmailRecipients: string[] = [];
    currentUserEmail: string;

    justSentTest = false;

    savePending: boolean = false;
    isSearchesLocked: boolean = false;
    openAiPermission: boolean = false;

    currentStep: DistributionStepsEnum = DistributionStepsEnum.compose;
    hidePersonalizeSidebar: boolean = false;

    // recipients
    readonly recipientTypes = RecipientType;
    additionalRecipients: string[];

    individualRecipientGroup: RecipientGroup;
    removedIndividualGroup: SearchModeEnum;

    boostBannerCollapsed: boolean = true;

    recommendationsGenerated: boolean = false;

    @ViewChild('contentScroll') contentScroll: ScrollPanelComponent;
    @ViewChild('blockFormTab') blockFormTab: TabComponent;
    @ViewChild('composeTab') composeTab: TabComponent;
    @ViewChild('editRecipientsTab') editRecipientsTab: TabComponent;
    @ViewChild('personalizeTab') personalizeTab: TabComponent;
    @ViewChild('reviewTab') reviewTab: TabComponent;
    @ViewChild('rightSidebar') rightSideBar: RightSidebarComponent;
    @ViewChildren(FormMessageComponent, { read: ElementRef }) formErrors: QueryList<ElementRef>;
    @ViewChild(PageBuilderComponent) pageBuilder: PageBuilderComponent;

    constructor(
        private coreDialogService: CoreDialogService,
        private currentUserService: CurrentUserStateService,
        private discoverRoutingService: DiscoverRoutingService,
        protected distributionService: DistributionService,
        private route: ActivatedRoute,
        private localizationService: LocalizationService,
        private mediaServiceApi: MediaServiceApi,
        private messageService: MessageService,
        private pageBuilderService: AbstractPageBuilderService,
        private recommendedRecipientService: RecommendedRecipientService,
        public recipientEditorService: RecipientEditorService,
        public titleService: Title,
        public ref: ElementRef,
        public injector: Injector,
        private targetingRhpContextsService: TargetingRhpContextsService,
        public dataConnectionService: DataConnectionService
    ) {
        const sendDistribution$ = this.sendDistributionSubject$.pipe(
            switchMap(distribution => {
                this.distribution.name = this.distribution.name?.trim();
                if (!this.distribution.name) {
                    this.setNewDistributionNameDialog(DistributionValidationLevel.SEND);
                    return NEVER;
                }
                if (this.validateAndSetErrorMessages(DistributionValidationLevel.SEND)) {
                    this.setBannerErrorList();
                    this.focusErrorSection();
                    return NEVER;
                }

                if (this.attachments.length) {
                    return from(this.uploadFiles()).pipe(
                        map(() => this.distribution)
                    );
                }
                return of(this.distribution);
            }),
            switchMap(() => this.removedIndividualsFromList()),
            switchMap(() => {
                const io: DialogIoModelObservable = {
                    outputs: {}
                };
                return this.coreDialogService.openWithObservable(DistributionConfirmationComponent, io);
            }),
        );

        this.subscriptions.push(
            sendDistribution$.subscribe(),
            this.distributionService.getSelectedBlock().subscribe(
                (block: BlockModel) => {
                    this.selectedBlock = block;
                    if (this.selectedBlock) {
                        // HACK: changing the tab index here doesn't actually select it unless the block form tab is already enabled
                        // This is because the disabled property in the template is handled after the tab index
                        // So we change the disabled state manually to handle that
                        if (this.blockFormTab) {
                            this.blockFormTab.disabled = false;
                        }
                        this.selectedRhpTabIndex = this.blockFormTabIndex;
                        if (block.type.key !== DistributionBlockTypeEnum.text && block.type.key !== DistributionBlockTypeEnum.footer) {
                            requestAnimationFrame(() => {
                                const blockForm = document.querySelector('agpr-block-form');
                                if (!blockForm) {

                                    return;
                                }
                                const firstInput = blockForm.querySelector('input, button, [role=button]');
                                (firstInput as HTMLElement)?.focus();
                            });
                        }
                    } else if (this.selectedRhpTabIndex === this.blockFormTabIndex) {
                        this.selectedRhpTabIndex = this.defaultSelectedRhpTabIndex;
                    }
                }
            ),
            this.discoverRoutingService.getCurrentRoute().subscribe(
                route => {
                    this.currentRoute = route;
                }
            ),
            this.distributionService.getFocusedResource().pipe(
                filter(x => !!x)
            ).subscribe(distribution => {
                if (distribution.status !== DistributionStatus.draft) {
                    this.discoverRoutingService.gotoDistributionReport(distribution.id);
                } else {
                    const distributionChanged = !this.distribution || distribution.id !== this.distribution.id;
                    this.distribution = distribution;
                    this.headerError = null;
                    this.recipientPanelError = null;
                    this.selectedRhpTabIndex = this.defaultSelectedRhpTabIndex;

                    if (distributionChanged) {
                        this.loadAttachmentChips();
                        this.errors = new DistributionErrors();
                        this.distributionService.clearAEORecommendations();
                    } else if (!this.distribution.id) {
                        this.errors = new DistributionErrors();
                    }
                    this.setAttributes();
                    this.updateSchedulableFlag();
                    if (!this.distribution?.id) {
                        this.setDefaultConfirmationRecipient();
                    }
                    this.maxWidthEnabled = !!this.distribution.generalStyles.distributionMaxWidthPx;
                    this.distributionMaxWidthPx = this.distribution.generalStyles.distributionMaxWidthPx;
                    this.setDistributionMaxWidth(this.distributionMaxWidthPx);
                }
                this.titleService.setTitle(
                    this.localizationService.transform('Discover.distribution.browserTitle.name', { name: distribution.name })
                );
            }),
            this.localizationService.getLocalizationMapChange().subscribe(() => this.setAttributes()),
            this.distributionService.getDirtyFlag().subscribe(dirty => {
                this.distributionDirty = dirty;
                this.setBannerErrorList();
            }),
            this.distributionService.getTestEmailSent().subscribe(testEmailSent => {
                // is only used when you sent a test email and save as draft just before
                // we do this because when you save to draft, it brings you to a new page, (with the dist ID)
                // once the page loads, we set the justSentTest to true & select the RHP tab to be the 2nd tab
                if (testEmailSent) {
                    this.justSentTest = testEmailSent;
                    this.selectedRhpTabIndex = 1;
                    // set it back to false so it doesn't do it always when switching between dists
                    this.distributionService.setTestEmailSent(false);
                }
            }),
            this.distributionService.getLoading().subscribe(x => {
                this.loading = x;
                if (!this.loading && this.savePending) {
                    this.savePending = false;
                }
            }),
            this.currentUserService.getUser().subscribe(x => {
                this.testEmailRecipients = [x.email];
                this.currentUserEmail = x.email;
                if (!this.distribution?.id) {
                    this.setDefaultConfirmationRecipient();
                }
            }),
            this.distributionService.getDefaultFooterDirty().subscribe(dirty => this.isDefaultFooterDirty = dirty),
            this.distributionService.getValidationErrors$().subscribe(errors => this.errors = errors),
            this.currentUserService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            }),
            this.recipientEditorService.getCurrentRecipientInformation().pipe(filter(x => !!x)).subscribe(recipientInfo => {
                this.additionalRecipients = cloneDeep(recipientInfo)?.getOrCreateGroup(RecipientType.email).emailRecipients;
                this.recipientInformation = recipientInfo;
                this.individualRecipientGroup = this.recipientInformation.recipientGroups.find(group => group.type === RecipientType.individual);

                const searchGroup = this.recipientInformation.recipientGroups.find(x => x.type === RecipientType.search);
                this.isSearchesLocked = searchGroup ? searchGroup.recipients.every(x => x.isLocked) : false;
            }),
            this.targetingRhpContextsService.getCurrentTab().subscribe((tabIndex: number) => {
                if (tabIndex === null) {
                    this.personalizationRhpTabIndex = 0;
                } else {
                    this.personalizationRhpTabIndex = tabIndex;
                }
            }),
            this.distributionService.getAEORecommendations().subscribe(recs => {
                this.recommendationsGenerated = recs.length > 0;
            })
        );

    }

    ngOnInit(): void {
        this.overlayContainer = document.getElementById('main-content');

        const newDistributionContentKey = this.route.snapshot.queryParamMap.get('newDistributionContentKey');
        if (newDistributionContentKey) {
            const stored = localStorage.getItem(newDistributionContentKey);
            if (stored) {
                localStorage.removeItem(newDistributionContentKey);
                const content = JSON.parse(stored);
                this.distributionService.createActionAssistantDistribution(content);
            }
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    pageUnload($event) {
        if (this.distributionDirty) {
            $event.returnValue = this.localizationService.transform('DeactivateGuard.messageText');
        }
    }

    setDefaultConfirmationRecipient() {
        if (this.distribution && this.currentUserEmail && this.distribution.confirmationRecipients.length === 0) {
            this.distribution.confirmationRecipients = [this.currentUserEmail];
        }
    }

    deliveryScheduleChanged(newSchedule: DeliverySchedule) {
        this.distribution.deliverySchedule = newSchedule;
        this.distributionService.updateDirtyFlag();
    }

    openContentLibrary(): void {

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger,
                overlayContainer: this.overlayContainer,
                maxFiles: this.maxNumberOfAttachments,
                selectionMode: true,
                navBarLaunch: false
            },
            outputs: {
                dialogClose: (selectedFiles: MediaSummaryModel[]) => {
                    if (!selectedFiles?.length) {
                        return;
                    }
                    const existingFilesSize = this.attachments?.reduce((sum, file) => sum + file.size, 0) || 0;
                    const newFilesSize = selectedFiles?.reduce((sum, file) => sum + file.size, 0);
                    const totalSize = existingFilesSize + newFilesSize;

                    if (totalSize > 5242880) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: this.localizationService.transform('Common.mediaService.fileUpload.maxSize.heading'),
                            detail: this.localizationService.transform('Common.mediaService.fileUpload.maxSize.message', {
                                name: selectedFiles.map(file => file.name).join(', ') })
                        });
                        return;
                    }

                    if ((this.attachments?.length + (selectedFiles?.length)) > this.maxNumberOfAttachments) {
                        this.messageService.add({
                            severity : messageSeverityEnum.error,
                            summary: this.localizationService.transform('Common.forms.fileUpload.error.tooManyFiles.summary'),
                            detail: this.localizationService.transform(
                                'Common.forms.fileUpload.error.tooManyFiles.detail',
                                { files: this.maxNumberOfAttachments }
                            )
                        });
                        return;
                    }

                    const fileUploads: FileUploadModel[] = selectedFiles.map(file => ({
                        id: file.id,
                        name: file.name,
                        size: file.size,
                        url: file.url,
                        mimeType: file.mimeType,
                    }));

                    const combinedFiles = [...(this.attachments || []), ...fileUploads];
                    this.attachmentChange(combinedFiles);
                }
            }
        };
        this.coreDialogService.open(ManageContentLibraryDialogComponent, io);
    }

    setNewDistributionNameDialog(distributionValidationLevel?: DistributionValidationLevel) {
        const textInputDialogConfig: NewResourceDialogModel = {
            inputs: {
                heading: 'Discover.dialogs.distributionName.heading',
                description: 'Discover.dialogs.distributionName.description',
                placeholder: 'Discover.dialogs.distributionName.placeholder',
                okLabel: 'Discover.dialogs.distributionName.save',
                cancelLabel: 'Discover.dialogs.universal.cancelLabel',
                width: 300,
                submit: (name: string, group?: DisplayGroupModel) => {
                    this.distribution.name = name;
                    this.distribution.groupId = group?.id;
                    if (distributionValidationLevel === DistributionValidationLevel.DRAFT) {
                        this.saveDistribution();
                    } else if (distributionValidationLevel === DistributionValidationLevel.SEND) {
                        this.send();
                    } else {
                        this.sendTestEmail();
                    }
                    return of(null);
                },
                formValidator: (distributionName: string) => {
                    this.distribution.name = distributionName.trim();
                    const { nameExists } = this.distributionService.getValidationErrors(this.distribution, DistributionValidationLevel.DRAFT);
                    this.distribution.name = null;

                    return nameExists ? ['Discover.targeting.distribution.error.duplicate'] : [];
                }
            },
            outputs: {
                dialogClose: () => {
                    this.savePending = false;
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, textInputDialogConfig);
    }

    async saveDistribution() {
        this.savePending = true;
        this.distribution.name = this.distribution.name?.trim();
        if (!this.distribution.name) {
            this.setNewDistributionNameDialog(DistributionValidationLevel.DRAFT);
            return;
        }
        if (this.validateAndSetErrorMessages(DistributionValidationLevel.DRAFT)) {
            this.savePending = false;
            if (this.currentStep === DistributionStepsEnum.compose) {
                this.focusFirstError();
            } else if (this.currentStep === DistributionStepsEnum.review) {
                this.focusErrorSection();
            }
            return;
        }
        // When user clicks on saving distribution without deselecting the rich text block, apply email style clean up before saving
        if (this.selectedBlock?.displayElement instanceof RichTextDisplayElementModel) {
            const selectedBlock = this.distribution.blocks.find(block => block.equals(this.selectedBlock));
            if (selectedBlock && !(<RichTextDisplayElementModel>selectedBlock.displayElement).htmlEdit) {
                (<RichTextDisplayElementModel>selectedBlock.displayElement).text
                    = EmailHelper.getTextContentStyledForEmail((<RichTextDisplayElementModel>selectedBlock.displayElement).text);
                this.pageBuilderService.formFieldUpdated();
            }
        }
        if (this.attachments.length > 0) {
            await this.uploadFiles();
        } else {
            this.distribution.attachments = [];
        }
        // we want to clear the results for Recommended recipients once we save the distribution
        this.recommendedRecipientService.clearResultsAfterSave();
        await lastValueFrom(this.removedIndividualsFromList());
        this.distributionService.saveDistribution().subscribe();
    }

    async send() {
        this.sendDistributionSubject$.next(this.distribution);
    }

    /**
     * Called by Send and saveDistribution to decide whether to continue with either action
     * @param distributionValidationLevel decides whether to validate everything (Send), or just the name (Draft)
     * @returns a boolean describing if any errors occurred during validation
     */
    validateAndSetErrorMessages(distributionValidationLevel: DistributionValidationLevel): boolean {
        this.errors = new DistributionErrors();
        const allErrors: DistributionErrors = this.distributionService.getValidationErrors(this.distribution, distributionValidationLevel);
        if (distributionValidationLevel === DistributionValidationLevel.SEND) {
            this.errors = allErrors;
        } else {
            this.errors.hasNoName = allErrors.hasNoName;
            this.errors.nameExists = allErrors.nameExists;
            this.errors.invalidBlocks = allErrors.invalidBlocks;
        }
        this.distributionService.setValidationErrors$(this.errors);
        return this.errors.getErrorCount() > 0;
    }

    focusErrorSection(): void {
        requestAnimationFrame(() => {
            if (!this.errors.isHeaderValid()) {
                document.querySelector<HTMLElement>('#distributionReview_editDetails_button')?.focus();
            } else if (this.compositionErrors.length > 0) {
                document.querySelector<HTMLElement>('#composition_error_lst')?.focus();
            } else if (this.errors.hasNoRecipients) {
                document.querySelector<HTMLElement>('#distributionReview_editRecipients_button')?.focus();
            } else if (this.errors.deliveryScheduleInvalid) {
                this.setErrorTabIndex();
            }
        });
    }

    /**
     * Finds the first error and focuses the related block or input, first being the first in the visual order of the page, top to bottom, left to right.
     * Precedence given as follows: header > content blocks > footer > rhp
     */
    focusFirstError() {
        requestAnimationFrame(() => {
            if (!this.errors.isHeaderValid()) {
                document.querySelector<HTMLElement>('button[id^=header]')?.focus();
            } else if (this.errors.emptyBlocks || this.errors.invalidBlocks) {
                this.pageBuilder.focusFirstBlockWithErrors();
            } else if (this.errors.footerMissingMergeTags) {
                // This error message is outside of PageBuilder so we need to focus the footer manually
                document.querySelector<HTMLElement>(`button[id^=block_${this.distribution.blocks.length - 1}]`)?.focus();
            } else { // center panel is clear, focus RHP error
                this.setErrorTabIndex();
                return;
            }
        });
    }

    setBannerErrorList() {
        const errorArray: StepsBannerErrorConfigModel[] = [];
        this.compositionErrors = [];
        let oldCompositionErrors = [];
        if (this.errors.headerHasInvalidEmail || this.errors.headerHasNoFrom || this.errors.headerHasNoReplyTo || this.errors.headerHasNoSubjectLine) {
            errorArray.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_HEADER_ERROR));
        }

        requestAnimationFrame(() => {
            if (this.errors.emptyBlocks || this.errors.invalidBlocks) {
                const result = this.distributionService.getBlockErrors(this.distribution.blocks);
                if (result.length) {
                    // need to keep a temp copy because when changing steps this method will get called twice, but initialization to [] only occur once
                    // so errors are pushed twice in this requestAnimationFrame
                    const newErrorArray = errorArray.slice();
                    const newCompositionErrors = oldCompositionErrors.slice();
                    for (let i = 0; i < result.length; i++) {
                        const error = {
                            errorId: result[i].id,
                            label: result[i].label,
                            tab: DistributionStepsEnum.compose
                        };

                        newErrorArray.push(error);
                        newCompositionErrors.push(error);
                        this.bannerErrorList = newErrorArray;
                        this.compositionErrors = newCompositionErrors;
                    }
                }
            }
        });

        if (this.errors.hasNoRecipients || this.errors.exceedsQuota) {
            errorArray.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_RECIPIENT_ERROR));
        }
        if (this.errors.footerHasNoCompany) {
            errorArray.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_COMPANY_ERROR));
            this.compositionErrors.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_COMPANY_ERROR));
        }
        if (this.errors.footerHasNoCity) {
            errorArray.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_CITY_ERROR));
            this.compositionErrors.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_CITY_ERROR));

        }
        if (this.errors.footerHasNoStreetAddress) {
            errorArray.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_STREETADDRESS_ERROR));
            this.compositionErrors.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_STREETADDRESS_ERROR));
        }
        if (this.errors.footerHasNoCountry) {
            errorArray.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_COUNTRY_ERROR));
            this.compositionErrors.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_COUNTRY_ERROR));
        }
        if (this.errors.footerHasNoZIP) {
            errorArray.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_POSTALCODE_ERROR));
            this.compositionErrors.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_POSTALCODE_ERROR));
        }
        if (this.errors.footerHasNoContact) {
            errorArray.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_CONTACT_ERROR));
            this.compositionErrors.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_CONTACT_ERROR));
        }

        if (this.errors.footerMissingMergeTags) {
            errorArray.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_MISSING_MERGE_TAGS_ERROR));
            this.compositionErrors.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_MISSING_MERGE_TAGS_ERROR));
        }

        if (this.errors.footerHasInvalidEmail) {
            errorArray.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_INVALID_EMAIL_ERROR));
            this.compositionErrors.push(bannerErrorsMap.get(DistributionBannerErrorTypeEnum.DISTRIBUTION_FOOTER_INVALID_EMAIL_ERROR));
        }

        this.bannerErrorList = errorArray;
        oldCompositionErrors = this.compositionErrors.slice();
    }


    setErrorTabIndex() {
        const footerTabErrorCount = Object.keys(this.errors)
            .filter(x => x.startsWith('footer'))
            .map(x => this.errors[x])
            .filter(Boolean)
            .length;
        const defaultTabErrorCount = this.errors.getErrorCount() - footerTabErrorCount;
        if (defaultTabErrorCount > 0) {
            this.selectedRhpTabIndex = this.defaultSelectedRhpTabIndex;
        } else if (footerTabErrorCount > 0) {
            this.pageBuilderService.selectBlock(this.distribution.blocks.find(x => x.type.key === DistributionBlockTypeEnum.footer));
        }
        requestAnimationFrame(() => {
            FocusHelper.focusFirstError(this.rightSideBar.container.nativeElement, '.agpr-form-message', this.rightSideBar.scrollPanel);
        });
    }

    openHeaderEditor() {
        /* If header errors exist when editor is opened, we store the state to
         * determine which ones have been changed once the editor is closed
         */
        const oldErrors = {
            headerHasNoFrom: this.errors.headerHasNoFrom,
            headerHasNoReplyTo: this.errors.headerHasNoReplyTo,
            headerHasInvalidEmail: this.errors.headerHasInvalidEmail,
            headerHasNoSubjectLine: this.errors.headerHasNoSubjectLine
        };

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        this.coreDialogService.open(AIDistributionHeaderDialogComponent, {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                openAiPermission: this.openAiPermission,
                title: 'Discover.targeting.distribution.headerEditor.header',
                aiReferenceId: this.distribution.aiReferenceId
            },
            outputs: {
                dialogClose: () => {
                    const {
                        headerHasNoFrom,
                        headerHasNoReplyTo,
                        headerHasInvalidEmail,
                        headerHasNoSubjectLine
                    } = this.distributionService.getValidationErrors(this.distribution, DistributionValidationLevel.DRAFT);

                    if (oldErrors.headerHasNoFrom) {
                        this.errors.headerHasNoFrom = headerHasNoFrom;
                    }
                    if (oldErrors.headerHasNoReplyTo) {
                        this.errors.headerHasNoReplyTo = headerHasNoReplyTo;
                    }
                    if (oldErrors.headerHasInvalidEmail) {
                        this.errors.headerHasInvalidEmail = headerHasInvalidEmail;
                    }
                    if (oldErrors.headerHasNoSubjectLine) {
                        this.errors.headerHasNoSubjectLine = headerHasNoSubjectLine;
                    }
                    this.distributionService.updateDirtyFlag();
                }
            }
        }, this.injector);
    }

    updateSchedulableFlag() {
        this.distributionSchedulable = true;
        this.distributionSchedulable = this.distributionSchedulable && this.distribution.status === DistributionStatus.draft;
    }

    enableMaxWidth(): void {
        if (this.maxWidthEnabled) {
            this.distributionMaxWidthPx = this.distributionMaxWidthPx || 640;
            this.setDistributionMaxWidth(this.distributionMaxWidthPx);
        } else {
            this.setDistributionMaxWidth(null);
            this.maxWidthOutOfRange = false;
        }
    }

    // when the value in number input changes, if it's out of range, don't do anything
    updateDistributionMaxWidth(distributionMaxWidthPx: number): void {
        if (distributionMaxWidthPx < this.minimumEmailWidthPx || distributionMaxWidthPx > this.maximumEmailWidthPx) {
            this.maxWidthOutOfRange = true;
            return;
        } else {
            this.maxWidthOutOfRange = false;
        }
        this.distributionMaxWidthPx = distributionMaxWidthPx;
        this.setDistributionMaxWidth(this.distributionMaxWidthPx);
    }

    setDistributionMaxWidth(distributionMaxWidthPx: number): void {
        this.distribution.generalStyles.distributionMaxWidthPx = distributionMaxWidthPx;
        this.distribution.blocks.forEach(block => {
            block.displayElement.styles['maxWidthPx'] = distributionMaxWidthPx;
        });
        this.distributionService.setBlocks(this.distribution.blocks);
        this.distributionService.setBlockMaxWidth(distributionMaxWidthPx);
    }

    constrainDistributionMaxWidth() {
        if (this.distributionMaxWidthPx < this.minimumEmailWidthPx) {
            this.distributionMaxWidthPx = this.minimumEmailWidthPx;
        }
        if (this.distributionMaxWidthPx > this.maximumEmailWidthPx) {
            this.distributionMaxWidthPx = this.maximumEmailWidthPx;
        }
    }

    async uploadFiles() {
        this.loading = true;
        this.attachments = await this.distributionService.uploadAttachments(this.attachments);
        this.distribution.attachments = this.attachments.map(file => file.id);
        this.distribution.localAttachmentChanges = false;
        this.loading = false;
    }

    attachmentChange(attachments: FileUploadModel[]) {
        // Since we use the Content Library to manage attachments, all selected attachments are uploaded and therefore have IDs.
        // Content Library attachments have IDs that are used to identify and remove duplicates
        const seenIds = new Set();
        const uniqueAttachments = attachments.filter(file => {
            if (seenIds.has(file.id)) {
                return false;
            }
            seenIds.add(file.id);
            return true;
        });

        this.hasDuplicateAttachments = uniqueAttachments.length !== attachments.length;
        this.attachments = uniqueAttachments;
        this.distribution.attachments = this.attachments.map(file => file.id);
        this.distribution.localAttachmentChanges = true;
        this.distributionService.updateDirtyFlag();
    }

    removeAttachment(attachment: FileUploadModel) {
        const distribIndex = this.distribution.attachments.findIndex(distributionAttachment => distributionAttachment === attachment.id);
        if (distribIndex >= 0) {
            this.distribution.attachments.splice(distribIndex, 1);
            this.messageService.add({
                severity: messageSeverityEnum.success,
                summary: 'Discover.distribution.attachmentRemoved.heading',
                detail: 'Discover.distribution.attachmentRemoved.message'
            });
            this.distributionService.updateDirtyFlag();
        }
    }

    loadAttachmentChips() {
        if (this.distribution.attachments.length > 0) {
            // note : There is no guarantee all the uuids will be found in the media service
            this.mediaServiceApi.getFiles(this.distribution.attachments).subscribe(mediaResults => {
                this.attachments = [];
                mediaResults.mediaResults.forEach(attachment => {
                    this.attachments.push({
                        name: attachment.name,
                        size: attachment.size,
                        type: attachment.mimeType,
                        id: attachment.id,
                        url: attachment.url
                    });
                });
            });
        } else {
            this.attachments = [];
        }
    }

    setAttributes() {
        if (!this.distribution) {
            this.distributionAttributes = [];
            return;
        }

        if (!this.distribution.createdDate) {
            this.distributionAttributes = [];
            return;
        }

        const nameAttribute: AttributeModel = {
            id: 0,
            label: 'Discover.targeting.distribution.rhp.distributionName',
            value: this.distribution.name,
            type: attributeTypesEnum.simple,
        };

        const createdByUserAttribute: AttributeModel = {
            id: 1,
            label: 'createdBy',
            value: this.distribution.createdByUserId,
            type: attributeTypesEnum.user,
        };

        const createdOnDateAttribute: AttributeModel = {
            id: 2,
            label: 'createdDate',
            value: this.distribution.createdDate,
            type: attributeTypesEnum.date,
        };

        const lastModifiedByUserAttribute: AttributeModel = {
            id: 3,
            label: 'lastModifiedBy',
            value: this.distribution.modifiedByUserId,
            type: attributeTypesEnum.user
        };

        const lastModifiedDateAttribute: AttributeModel = {
            id: 4,
            label: 'lastModifiedDate',
            value: this.distribution.modifiedDate,
            type: attributeTypesEnum.date,
        };

        this.distributionAttributes = [
            nameAttribute,
            createdByUserAttribute,
            createdOnDateAttribute,
            lastModifiedByUserAttribute,
            lastModifiedDateAttribute
        ].filter(x => !!x.value);
    }

    scrollToAddedBlock(block: ElementRef): void {
        this.contentScroll.scrollIntoView(block);
    }

    openDevicePreview(screenType: ScreenType) {
        this.selectedScreenType = screenType;
        this.coreDialogService.open(DevicePreviewDialogComponent, {
            inputs: {
                selectedScreenType: screenType
            },
            outputs: {
                selectedScreenTypeChanged: (st: ScreenType) => {
                    this.selectedScreenType = st;
                }
            }
        });
    }

    sendTestEmail() {
        this.distribution.name = this.distribution?.name?.trim();
        if (!this.distribution.name) {
            this.setNewDistributionNameDialog();
            return;
        }
        if (this.validateAndSetErrorMessages(DistributionValidationLevel.DRAFT)) {
            return;
        }

        this.distributionService.sentTestDistribution(this.testEmailRecipients);
        this.justSentTest = true;
    }

    openPlainTextEditor() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        this.coreDialogService.open(PlainTextEditorDialogComponent, {
            inputs: {
                setDistribution: this.distribution,
                overlayContainer: this.overlayContainer,
                animationTrigger
            },
            outputs: {
                dialogClose: (plainText: { bodyPlainText: string; footerPlainText: string }) => {
                    if (plainText) {
                        this.distribution.bodyPlainText = plainText.bodyPlainText;
                        this.distribution.footerPlainText = plainText.footerPlainText;
                        this.distributionService.updateDirtyFlag();
                    }
                }
            }
        });
    }

    saveAsTemplate(): void {
        this.coreDialogService.open(DistributionSaveTemplateDialogComponent);
    }

    public changeStep(tabIndex: number) {
        this.currentStep = tabIndex;
        this.selectedRhpTabIndex = this.defaultSelectedRhpTabIndex;
        this.validateAndSetErrorMessages(DistributionValidationLevel.SEND);
        this.setBannerErrorList();
        if (tabIndex === DistributionStepsEnum.compose && (this.errors.emptyBlocks || this.errors.invalidBlocks)) {
            this.distributionService.notifyBlockDisplayElementChanged();
        }
    }

    openRecipientViewer(recipient: DistributionRecipientModel) {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const dialogIO = {
            inputs: {
                recipient: recipient,
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
            }
        };
        this.coreDialogService.open(RecipientViewerComponent, dialogIO, this.injector);
    }

    additionalRecipientsChanged() {
        this.recipientInformation.getOrCreateGroup(this.recipientTypes.email).emailRecipients = this.additionalRecipients;
        this.recipientEditorService.setEmailRecipientCount();
    }

    viewIndividualRecipients(searchMode: SearchModeEnum) {
        const indv = this.individualRecipientGroup.recipients?.[0].clone();
        indv.searchMode = searchMode;
        this.openRecipientViewer(indv);
    }

    removeIndividualRecipients(searchMode: SearchModeEnum) {
        const total = Math.max(this.individualRecipientGroup.recipients?.[0].contactCount, this.individualRecipientGroup.recipients?.[0].outletCount);

        if (this.removedIndividualGroup || this.individualRecipientGroup.recipients?.[0].count === total) {
            this.recipientEditorService.removeRecipientByTrashIcon(this.individualRecipientGroup.recipients?.[0]);
            this.removedIndividualGroup = null;
        } else {
            this.removedIndividualGroup = searchMode;
        }
    }

    removedIndividualsFromList(): Observable<void> {
        if (this.removedIndividualGroup && this.individualRecipientGroup.recipients?.[0]) {
            const targetingListId = this.individualRecipientGroup.recipients?.[0].targetingListId;

            const search = new TargetingSearch();
            search.list.include = [targetingListId];
            search.searchMode = this.removedIndividualGroup;

            return this.distributionService.removedIndividualsFromList(targetingListId, search.toApiModel().query).pipe(
                tap({
                    next: () => {
                        this.removedIndividualGroup = null;
                    },
                    error: () => {
                        this.removedIndividualGroup = null;
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Discover.individualRecipients.error.removing.title',
                            detail: 'Discover.individualRecipients.error.removing.message'
                        });
                    }
                })
            );
        }
        return of(null);
    }

    selectRecipientTab() {
        this.personalizeTab.selected = false;
        this.editRecipientsTab.selected = true;
        this.currentStep = DistributionStepsEnum.editRecipients;
    }

    navigateToRecipientStep() {
        this.currentStep = DistributionStepsEnum.editRecipients;
    }

    unlockSearch(searchIds: number[]) {
        const io = {
            inputs: {
                heading: 'Discover.recipientEditor.confirmDialog.confirmUnlock',
                message: 'Discover.recipientEditor.confirmDialog.body1',
                bottomMessage: 'Discover.recipientEditor.confirmDialog.body2',
                alertMessage: 'Discover.distribution.saveLockSearchDialog.warning',
                alertType: messageSeverityEnum.warning,
                okLabel: 'Discover.recipientEditor.confirmDialog.unlock',
                bottomMargin: true
            },
            outputs: {
                dialogClose: (confirm: boolean) => {
                    if (confirm) {
                        this.recipientEditorService.toggleSearches(searchIds, ToggleAction.UNLOCK).subscribe();
                    }
                }
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    unlockAllSearches() {
        const searchGroup = this.recipientInformation.recipientGroups.find(x => x.type === RecipientType.search);
        const searchIds = searchGroup.recipients.map(x => x.targetingSearchId);

        const io = {
            inputs: {
                heading: 'Discover.recipientEditor.confirmDialog.confirmUnlock',
                message: 'Discover.recipientEditor.confirmDialog.body1',
                bottomMessage: 'Discover.recipientEditor.confirmDialog.body2',
                alertMessage: 'Discover.distribution.saveLockSearchDialog.warning',
                alertType: messageSeverityEnum.warning,
                okLabel: 'Discover.recipientEditor.confirmDialog.unlock',
                bottomMargin: true
            },
            outputs: {
                dialogClose: (confirm: boolean) => {
                    if (confirm) {
                        this.recipientEditorService.toggleSearches(searchIds, ToggleAction.UNLOCK).subscribe();
                    }
                }
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    lockAllSearches() {
        const searchGroup = this.recipientInformation.recipientGroups.find(x => x.type === RecipientType.search);
        const searchIds = searchGroup.recipients.filter(x => !x.isLocked).map(x => x.targetingSearchId);
        this.lockSearch(searchIds);
    }

    lockSearch(searchIds: number[]) {
        if (this.distribution.id) {
            this.distributionService.setLoading(true);
            this.distributionService.saveDistributionWithoutRefetch().pipe(
                switchMap((distId) => {
                    return this.recipientEditorService.toggleSearches(searchIds, ToggleAction.LOCK, distId);
                }),
                finalize(() => {
                    this.distributionService.setLoading(false);
                })
            ).subscribe(() => this.navigateToRecipientStep());
        } else {
            const io = {
                inputs: {},
                outputs: {
                    updateDistName: (name: string) => {
                        this.distribution.name = name;
                        this.distributionService.setFocusedDistribution(this.distribution);
                    },
                    dialogClose: (confirm: boolean) => {
                        if (confirm) {
                            this.distributionService.saveDistributionWithoutRefetch().pipe(
                                switchMap((distId) => {
                                    return this.recipientEditorService.toggleSearches(searchIds, ToggleAction.LOCK, distId);
                                })
                            ).subscribe(() => this.navigateToRecipientStep());
                        }
                    }
                }
            };
            this.coreDialogService.open(LockSearchDialogComponent, io);
        }
    }

    includeFooterChanged(): void {
        if (this.distribution.includeFooter) {
            this.distributionService.updateDirtyFlag();
            this.distribution.footer = new FooterModel();
            this.distribution.setFooterBlock(this.distribution.footer);
        } else {
            const io = {
                inputs: {
                    heading: 'Discover.distribution.otherSettings.includeFooter.confirmation.heading',
                    message: 'Discover.distribution.otherSettings.includeFooter.confirmation.body',
                    alertMessage: 'Discover.distribution.otherSettings.includeFooter.confirmation.warning',
                    alertType: messageSeverityEnum.warning,
                    okLabel: 'Discover.distribution.otherSettings.includeFooter.confirmation.heading',
                    bottomMargin: true,
                    disclaimerText: 'Discover.distribution.otherSettings.includeFooter.confirmation.disclaimer'
                },
                outputs: {
                    dialogClose: (confirm: boolean) => {
                        if (confirm) {
                            this.distribution.includeFooter = false;
                            this.distribution.footer = null;
                            this.distribution.footerPlainText = null;

                            this.distribution.blocks = this.distribution.blocks?.filter(x => x.type.key !== DistributionBlockTypeEnum.footer);
                            this.distributionService.setBlocks(this.distribution.blocks);
                            this.distributionService.updateDirtyFlag();
                        } else {
                            this.distribution.includeFooter = true;
                        }
                    }
                }
            };

            this.coreDialogService.open(ConfirmDialogComponent, io);
        }
    }

    onlineBoostChange() {
        if (this.distribution.onlineBoostApproved) {
            const animationTrigger = new BehaviorSubject(
                new FullProfileAnimationModel(
                    FullProfileDialogAnimationsEnum.slideInOutAnimation,
                    OverlayAnimationStateEnum.open
                )
            );
            const dialogIO = {
                inputs: {
                    animationTrigger: animationTrigger,
                    overlayContainer: this.overlayContainer,
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.distributionService.updateDirtyFlag();
                        } else {
                            this.distribution.onlineBoostApproved = false;
                        }
                    }
                }
            };
            this.coreDialogService.open(OnlineBoostConfirmationDialogComponent, dialogIO);
        } else {
            this.distributionService.updateDirtyFlag();
        }
    }

    async openAEODialog(regenerate: boolean = false) {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const dialogIO = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer
            }
        };
        if (!this.recommendationsGenerated || regenerate) {
            this.distributionService.generateAEORecommendations();
        }
        this.coreDialogService.open(AEORecommendationsDialogComponent, dialogIO);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
